import React, { useEffect, useState, useRef } from "react";
import "./dataSettings.css";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { fetchUnitCostMaster, fetchUnitMaster, fetchPlantTypes, fetchFuelTypes, commitUnitMaster, fetchLocalPlantUnit, commitCostMaster } from "./api";
import Table from "./table";
import { toast } from 'react-toastify';

const DataSettings = () => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [selectedOption, setSelectedOption] = useState("Unit Cost Master");
    const dropdownRef = useRef(null);
    const options = ["Unit Cost Master", "Unit Master"];
    const [headers, setHeaders] = useState([]);
    const [tableData, setTableData] = useState([]);
    const tableRef = useRef(null);
    const [plantTypeOptions, setPlantTypeOptions] = useState([]);
    const [fuelTypeOptions, setFuelTypeOptions] = useState([]);
    const [plantUnitOptions, setPlantUnitOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        callApiForOption(selectedOption);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setOpenDropdown(null);
        }
    };

    const toggleDropdown = (type) => {
        setOpenDropdown(openDropdown === type ? null : type);
    };

    const handleSelect = (option, type) => {
        if (type === "market") {
            setSelectedOption(option);
            callApiForOption(option);
        }
        setOpenDropdown(null);
    };

    const callApiForOption = async (option) => {
           setLoading(true);

        try {
            let data;

            // Fetch plant-unit data once since it's used in both branches
            const plantUnitResponse = await fetchLocalPlantUnit();
            const plantUnits = plantUnitResponse?.data || [];
            setPlantUnitOptions(plantUnits);
            const plants = await fetchPlantTypes();
            const fuels = await fetchFuelTypes();

            setPlantTypeOptions(plants.data);
            setFuelTypeOptions(fuels.data);
            if (option === "Unit Cost Master") {
                data = await fetchUnitCostMaster();

                if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
                    setHeaders([]);
                    setTableData([]);
                    processJsonData(data);
                } else {
                    // console.warn("API returned empty or invalid data:", data);
                }
            } else {
                data = await fetchUnitMaster();

                if (data?.data && Array.isArray(data.data) && data.data.length > 0) {

                    setHeaders([]);
                    setTableData([]);
                    processJsonData(data);
                } else {
                    // console.warn("Unit Master API returned empty data, setting default headers.");

                    const customHeaders = [
                        { name: 'plant_name', dataType: 'string', editable: true, width: 150, displayName: 'Plant Name' },
                        { name: 'unit_name', dataType: 'number', editable: true, width: 150, displayName: 'Unit Name' },
                        { name: 'plant_type', dataType: 'string', editable: true, width: 150, displayName: 'Plant Type' },
                        { name: 'fuel_type', dataType: 'string', editable: true, width: 150, displayName: 'Fuel Type' },
                        { name: 'total_capacity', dataType: 'number', editable: true, width: 150, displayName: 'Total Capacity' },
                        { name: 'contracted_capacity', dataType: 'number', editable: true, width: 150, displayName: 'Contracted Capacity' },
                        { name: 'min_technical_limit', dataType: 'number', editable: true, width: 150, displayName: 'Minimum technical limit' },
                        { name: 'time_between_ramp_up_down', dataType: 'number', editable: true, width: 150, displayName: 'Time between ramp up down' },
                        { name: 'ramp_rate_mw_per_min', dataType: 'number', editable: true, width: 150, displayName: 'Ramp rate MW per minute' },
                        { name: 'mustrun', dataType: 'boolean', editable: true, width: 150, displayName: 'Must Run' }
                    ];

                    const plants = await fetchPlantTypes();
                    const fuels = await fetchFuelTypes();

                    setPlantTypeOptions(plants.data);
                    setFuelTypeOptions(fuels.data);
                    setHeaders(customHeaders);
                    setTableData([]);
                }
            }
        } catch (error) {
            // console.error("API Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const processJsonData = (response) => {
        const data = response.data;
        if (!Array.isArray(data) || data.length === 0) {
            //   console.error("Invalid or empty data format");
            return;
        }

        const processedData = [];
        const columnDataTypes = {};
        const nonEditableColumns = new Set();
        const columnsWithNull = new Set();
        const uniqueHeaders = new Set();

        const formatDate = (dateStr) => {
            const date = new Date(dateStr);
            if (isNaN(date)) return dateStr;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const day = String(date.getDate()).padStart(2, "0");
            return `${year}/${month}/${day}`;
        };

        data.forEach((row) => {
            const newRow = {};

            Object.entries(row).forEach(([key, value]) => {
                let finalValue = value;

                uniqueHeaders.add(key);

                if (value === null) {
                    finalValue = "null";
                    columnsWithNull.add(key);
                    columnDataTypes[key] = columnDataTypes[key] || "string";
                } else if (typeof value === "boolean") {
                    columnDataTypes[key] = "boolean";
                } else if (!isNaN(value) && value !== "") {
                    finalValue = parseFloat(value);
                    columnDataTypes[key] = columnDataTypes[key] || "number";
                } else if (!isNaN(Date.parse(value))) {
                    finalValue = formatDate(value);
                    columnDataTypes[key] = columnDataTypes[key] || "date";
                } else {
                    columnDataTypes[key] = columnDataTypes[key] || "string";
                }

                newRow[key] = finalValue;

                if (["block_no"].includes(key.toLowerCase())) {
                    nonEditableColumns.add(key);
                }
            });

            processedData.push(newRow);
        });

        processedData.sort((a, b) => {
            if (a.id !== undefined && b.id !== undefined) {
                return a.id - b.id;
            }
            return 0;
        });

        const headersWithMetadata = Array.from(uniqueHeaders)
            .sort((a, b) => {
                if (a.toLowerCase() === "id") return -1;
                if (b.toLowerCase() === "id") return 1;
                if (nonEditableColumns.has(a) && !nonEditableColumns.has(b)) return -1;
                if (!nonEditableColumns.has(a) && nonEditableColumns.has(b)) return 1;
                return 0;
            })
            .map((key) => ({
                name: key,
                dataType: columnDataTypes[key] || "string",
                editable: !nonEditableColumns.has(key),
                width: 150,
                displayName: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            }));

        setHeaders(headersWithMetadata);
        setTableData(processedData);
    };


    const handleCommit = async () => {
        if (!tableRef.current?.getUpdatedData) return;

        let updatedData = tableRef.current.getUpdatedData();

        let formattedData = [];
        let requiredFields = [];

        if (selectedOption === "Unit Master") {
            requiredFields = [
                "plant_name",
                "unit_name",
                "plant_type",
                "fuel_type",
                "total_capacity",
                "contracted_capacity",
                "min_technical_limit",
                "time_between_ramp_up_down",
                "ramp_rate_mw_per_min",
                "must_run"
            ];

            formattedData = updatedData.map((item) => {
                const cleanItem = {};
                for (const key in item) {
                    if (key !== "[object Object]") {
                        cleanItem[key] = item[key];
                    }
                }
                return requiredFields.map(field => cleanItem[field]);
            });
        } else if (selectedOption === "Unit Cost Master") {
            requiredFields = [
                "valid_from",
                "valid_to",
                "station_name",
                "unit_name",
                "must_run",
                "fixed_cost",
                "variable_cost"
            ];

            formattedData = updatedData.map((item) => {
                const cleanItem = {};
                for (const key in item) {
                    if (key !== "[object Object]") {
                        cleanItem[key] = item[key];
                    }
                }
                return requiredFields.map(field => cleanItem[field]);
            });
        } else {
            return;
        }


        const newRows = updatedData.filter(row => row._isNew);

        const formattedNewData = newRows.map((item) => {
            const cleanItem = {};
            for (const key in item) {
                if (key !== "[object Object]") {
                    cleanItem[key] = item[key];
                }
            }
            return requiredFields.map(field => cleanItem[field]);
        });

        const hasInvalidValues = formattedNewData.some(row =>
            row.some(value => value === null || value === undefined || value === "")
        );

        if (hasInvalidValues) {
            alert("Some fields are empty or invalid in newly added rows. Please complete them.");
            return;
        }

        try {
            if (selectedOption === "Unit Master") {
                await commitUnitMaster(formattedData);
                toast.success("Unit Master data committed successfully.");
            } else if (selectedOption === "Unit Cost Master") {
                await commitCostMaster(formattedData);
                toast.success("Unit Cost Master data committed successfully.");
            }
        } catch (error) {
            toast.error("Failed to commit data.");
        }

    };

    return (
        <div className="positionmap-container" ref={dropdownRef}>
             {loading && (
                <div className="dashboard-loading-bar"></div>
            )}
            <div className="top-bar">
                <div className="dropdown-container">
                    <button onClick={() => toggleDropdown("market")} className="dropdown-button">
                        {selectedOption} {openDropdown === "market" ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}
                    </button>

                    {openDropdown === "market" && (
                        <div className="dropdown-menus">
                            {options.map((option, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleSelect(option, "market")}
                                    className={`dropdown-item-settings ${selectedOption === option ? "selected" : ""}`}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <br />

            <Table
                ref={tableRef}
                headers={headers}
                 maxHeight="550px"
                rows={tableData}
                edit={true}
                sorting={true}
                drag={false}
                filtering={true}
                exporting={true}
                addRow={true}
                restrictEditToNewRows={false}
                plantTypeOptions={plantTypeOptions}
                fuelTypeOptions={fuelTypeOptions}
                plantUnitOptions={plantUnitOptions}
                syncDates ={true}
            />

            <div className="dataImport-commit-container">
                <div
                    className="dataImport-commit"
                    onClick={handleCommit}
                >
                    COMMIT
                </div>
            </div>
        </div>
    );
};

export default DataSettings;
